import { useEffect, useState, useRef } from 'react';
import {
  Box, TextField, MenuItem, Button,
  Checkbox, FormControlLabel, Typography,
  FormControl, InputLabel, Select, Paper, Grid,
  CircularProgress
} from '@mui/material';
import { ApiServices } from '../../utils/ApiService';
import { useSnackbar } from 'notistack';
import { useParams } from "react-router-dom";

const fieldStyle = { marginBottom: '20px' };

const registerFileData = {
  fileName: '',
  propertyOwnerName: '',
  bankName: '',
  bankBranch: '',
  bankProduct: '',
  bankEmail: '',
  applicationId: '',
  titleDeedNo: '',
  surveyNo: '',
  extent: '',
  reportType: '',
  loginType: '',
  yaazhBranch: ''
};

const arrDocumentCategory = [
  'Title deed', 'Mother Document', 'Sale Agreement',
  'Revenue record', 'EC'
];

const arrLoginType = ['Normal', 'BT/ Takeover', 'Purchase', 'Topup', 'Type 1', 'Type 2'];

const arrCaseType = [
  'HL', 'LAP', 'HL-BT', 'LAP-BT', 'HL-PURCHASE', 'LAP-PURCHASE',
  'LAP-BT PURCHASE', 'HL-BT PURCHASE', 'SELLER BT PURCHASE',
  'HL TOPUP', 'LAP TOPUP', 'SUPPLEMENTARY LEGAL', 'Report 1', 'Report 2'
];

const EditCreateFilePage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const hasFetched = useRef(false);

  const [data, setData] = useState({ ...registerFileData });
  const [errors, setErrors] = useState({});
  const [createdBy, setCreatedBy] = useState(null);
  const [filesByCategory, setFilesByCategory] = useState({});
  const [arrBankCategory, setArrBankCategory] = useState([]);
  const [arrBankBranchCategory, setArrBankBranchCategory] = useState([]);
  const [arrBankProductCategory, setArrBankProductCategory] = useState([]);
  const [arrBranches, setArrBranches] = useState([]); // Yaazh Branches
  const [loading, setLoading] = useState(true);

  const [sameAsApplicant, setSameAsApplicant] = useState(false);
  const handleSameAsApplicant = (e) => {
    const checked = e.target.checked;
    setSameAsApplicant(checked);

    setData(prev => ({
      ...prev,
      propertyOwnerName: checked ? prev.fileName : prev.propertyOwnerName
    }));
  };

  // useEffect(() => {
  //   const user = JSON.parse(localStorage.getItem('user')) || {};
  //   const file_tab = user.file_tab || [];
  //   const branch_tab = user.branch_tab || [];

  //   setCreatedBy(Number(user?.id));
  //   setArrBankCategory(file_tab.map(obj => obj.bank_name));
  //   setArrBranches(branch_tab.filter(b => b.branch?.trim()));
  // }, []);

  useEffect(() => {
    if (!hasFetched.current) {
      downloadAndPopulateData();
      hasFetched.current = true;
    }
  }, []);

  const downloadAndPopulateData = async () => {
    setLoading(true);
    try {
      const res = await ApiServices.get(`file/download/${id}`);
      if (res?.status === 200) {
        const file = res?.data;
        
        const user = JSON.parse(localStorage.getItem('user')) || {};
        const file_tab = user.file_tab || [];
        setArrBankCategory(file_tab?.map(obj => obj.bank_name));

        const objBank = file_tab.find(obj => obj.bank_name === file.bank_name);
        setArrBankBranchCategory(objBank?.branches?.map(b => b.branch_name) || []);
        setArrBankProductCategory(objBank?.products || []);

        const branch_tab = user.branch_tab || [];
        setArrBranches(branch_tab?.map(b => b.branch?.trim()));

        setData({
          fileName: file.file_name || '',
          propertyOwnerName: file.property_owner_name || '',
          bankName: file.bank_name || '',
          bankBranch: file.bank_branch || '',
          bankProduct: file.bank_product || '',
          bankEmail: file.bank_email || '',
          applicationId: file.application_id || '',
          titleDeedNo: file.title_deed_no || '',
          surveyNo: file.survey_no || '',
          extent: file.extent || '',
          loginType: file.login_type || '',
          yaazhBranch: file.yaazh_branch || ''
        });

        if (file.file_name === file.property_owner_name) {
          setSameAsApplicant(true);
        }
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('No data found.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, key) => {
    const value = e.target.value;
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const file_tab = user.file_tab || [];

    if (key === 'bankName') {
      const objBank = file_tab.find(obj => obj.bank_name === value);
      setArrBankBranchCategory(objBank?.branches?.map(b => b.branch_name) || []);
      setArrBankProductCategory(objBank?.products || []);
      setData(prev => ({ ...prev, [key]: value, bankBranch: '', bankEmail: '' }));
    } else if (key === 'bankBranch') {
      const objBank = file_tab.find(obj => obj.bank_name === data.bankName);
      const objBranch = objBank?.branches?.find(b => b.branch_name === value);
      setData(prev => ({ ...prev, [key]: value, bankEmail: objBranch?.email || '' }));
    } else {
      setData(prev => {
        const updated = { ...prev, [key]: value };

        // Sync logic
        if (key === 'fileName' && sameAsApplicant) {
          updated.propertyOwnerName = value;
        }

        return updated;
      });
    }

    if (value.trim()) {
      setErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  const handleCategoryFileChange = (e, category) => {
    const selectedFiles = e.target.files;
    setFilesByCategory(prev => ({
      ...prev,
      [category]: selectedFiles
    }));

    if (selectedFiles.length > 0) {
      setErrors(prev => ({ ...prev, [`files_${category}`]: '' }));
    }
  };

  const renderTextField = (label, key, disabled = false) => (
    <TextField
      fullWidth
      size="medium"
      variant="outlined"
      style={fieldStyle}
      label={label}
      value={data[key]}
      onChange={e => handleChange(e, key)}
      error={!!errors[key]}
      helperText={errors[key]}
      disabled={disabled}
      InputProps={{
        readOnly: disabled,
        style: disabled ? { backgroundColor: '#f5f5f5' } : {}
      }}
    />
  );

  const renderSelectField = (label, key, options) => (
    <FormControl fullWidth size="medium" style={fieldStyle} error={!!errors[key]}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={data[key]} onChange={e => handleChange(e, key)}>
        {options?.map((item, idx) => (
          <MenuItem value={item} key={idx}>
            {item}
          </MenuItem>
        ))}
      </Select>
      {errors[key] && (
        <Typography color="error" variant="caption">
          {errors[key]}
        </Typography>
      )}
    </FormControl>
  );

  const renderFileUploadField = (category) => (
    <Box key={category} style={fieldStyle}>
      <Typography variant="subtitle1">{category}</Typography>
      <Button variant="contained" component="label" fullWidth size="medium">
        Upload {category}
        <input
          type="file"
          hidden
          multiple
          onChange={e => handleCategoryFileChange(e, category)}
        />
      </Button>
      {errors[`files_${category}`] && (
        <Typography color="error" variant="caption">
          {errors[`files_${category}`]}
        </Typography>
      )}
      {filesByCategory[category] && filesByCategory[category].length > 0 && (
        <Box mt={1}>
          {Array.from(filesByCategory[category])?.map((file, idx) => (
            <Typography key={idx} variant="body2">
              • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setLoading(true);
      const formData = new FormData();

      Object.entries(filesByCategory).forEach(([category, fileList]) => {
        Array.from(fileList).forEach(file => {
          formData.append(`files_${category.replace(/\s+/g, '_')}`, file);
        });
      });

      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const res = await ApiServices.put(`file/uploads/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res?.status === 200) {
        enqueueSnackbar('File updated successfully', { variant: 'success' });
        setErrors({});
      } else {
        enqueueSnackbar('Update failed', { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Error during update', { variant: 'warning' });
    }
    finally
    {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#f0f2f5', py: 5 }}>
      {loading ? (
        <Grid container justifyContent="center" alignItems="center" style={{ height: "100%" }}>
          <CircularProgress />
        </Grid>
      ) : (
        <Paper elevation={4} sx={{ maxWidth: 600, mx: 'auto', p: 4, borderRadius: 3, background: 'white' }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>Edit File</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>Modify the required details.</Typography>

          {renderTextField('Applicant Name', 'fileName')}

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <FormControlLabel
              label="Same as Applicant Name"
              labelPlacement="start"
              control={
                <Checkbox
                  checked={sameAsApplicant}
                  onChange={handleSameAsApplicant}
                />
              }
            />
          </Box>

          {renderTextField('Property Owner Name', 'propertyOwnerName', sameAsApplicant)}
          {renderSelectField('Bank Name', 'bankName', arrBankCategory)}
          {renderSelectField('Bank Branch', 'bankBranch', arrBankBranchCategory)}
          {renderTextField('Bank Mail ID', 'bankEmail', true)}
          {renderSelectField('Report Type', 'bankProduct', arrBankProductCategory)}
          {renderTextField('Application ID / LAN & LOS No', 'applicationId')}
          {renderTextField('Title Deed No', 'titleDeedNo')}
          {renderTextField('Survey No', 'surveyNo')}
          {renderTextField('Extent', 'extent')}
          {renderSelectField('Login Type', 'loginType', arrLoginType)}

          {/* Yaazh Branch Field */}
          {renderSelectField('Yaazh Branch', 'yaazhBranch', arrBranches)}

          {arrDocumentCategory?.map(category => renderFileUploadField(category))}

          <Box mt={3} display="flex" justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 5, py: 1.5, fontWeight: 'bold',
                borderRadius: 2,
                background: '#1976d2',
                '&:hover': { background: '#1565c0' }
              }}
              onClick={handleSubmit}
            >
              Save File
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default EditCreateFilePage;
